import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import type { CardData } from 'types/story';
import type { ClientReducerState } from 'client/reducers';
import type { ISelectionHintContext } from 'client/components/common/SelectionHint/SelectionHintContext';
import { clientLog, defer } from 'common/utils/helpers';
import { location } from 'common/utils/url-helper';
import { CARD_TYPE } from 'common/constants';
import { StoryFacade } from 'common/utils/facades/story-facade';
import { CardFacade } from 'common/utils/facades/card-facade';
import { AnswerDelta, StoryHistory, SubmissionDelta } from 'client/utils';
import { sendGameCheckpoint } from 'client/actions/api';
import { CARD_ROOT_ID } from 'client/constants/common';
import { selectStory, selectStoryCards } from 'client/reducers/story/selectors';
import InfoCard from 'client/components/common/StoryCard/Info';
import TriviaCard from 'client/components/common/StoryCard/Trivia';
import SortableTriviaCard from 'client/components/common/StoryCard/SortableTrivia';
import SortablePollCard from 'client/components/common/StoryCard/SortablePoll';
import PollCard from 'client/components/common/StoryCard/Poll';
import PersonalityTestCard from 'client/components/common/StoryCard/PersonalityTest';
import TrueOrFalseCard from 'client/components/common/StoryCard/TrueOrFalse';
import ThisOrThatCard from 'client/components/common/StoryCard/ThisOrThat';
import NavigationCard from 'client/components/common/StoryCard/Navigation';
import FormCard from 'client/components/common/StoryCard/Form';
import RegistrationCard from 'client/components/common/StoryCard/Registration';
import SandboxCard from 'client/components/common/StoryCard/Sandbox';
import { ApiEvents } from 'client/ApiEvents';
import CardHeightObserver from './CardHeightObserver';
import css from './Card.scss';

const log = clientLog.extend('Card');

const { isPreview } = location.client;

const mapState = (state: ClientReducerState) => ({
	isSingleCardStory: (selectStoryCards(state)?.length ?? 1) === 1,
	organizationId: selectStory(state)!.organizationId,
	storyId: selectStory(state)!.id,
	user: state.user,
});

const connector = connect(mapState, { sendGameCheckpoint });

const MAP = {
	[CARD_TYPE.INFO]: InfoCard,
	[CARD_TYPE.TRIVIA]: TriviaCard,
	[CARD_TYPE.SORTABLE_TRIVIA]: SortableTriviaCard,
	[CARD_TYPE.SORTABLE_POLL]: SortablePollCard,
	[CARD_TYPE.POLL]: PollCard,
	[CARD_TYPE.PERSONALITY_TEST]: PersonalityTestCard,
	[CARD_TYPE.TRUE_OR_FALSE]: TrueOrFalseCard,
	[CARD_TYPE.THIS_OR_THAT]: ThisOrThatCard,
	[CARD_TYPE.NAVIGATION]: NavigationCard,
	[CARD_TYPE.FORM]: FormCard,
	[CARD_TYPE.REGISTRATION]: RegistrationCard,
	[CARD_TYPE.SANDBOX]: SandboxCard,
};

type Props = ConnectedProps<typeof connector> & {
	children?: React.ReactNode;
	card: CardData;
	nextDefaultCardId?: string;
	onCardClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
	isEditor?: boolean;
	story: StoryFacade;
	treeOptions:
		| { target: 'default'; isCardTree: true }
		| ({ target: 'editor'; isCardTree: true } & ISelectionHintContext['editor']['treeOptions']);
};

interface EventsI {
	isValidProps: (props: Props) => boolean;
	getEventParams: (props: Props) => {
		organizationId: string;
		storyId: string;
		cardId: string;
		teamId: string;
		memberId: string;
	};
}
const events: EventsI = {
	isValidProps(props) {
		const { user, organizationId, storyId, card } = props;
		const hasData = organizationId && storyId && card._id;
		return !!hasData && !!user.isRegistered;
	},
	getEventParams(props) {
		return {
			organizationId: props.story.base.organizationId,
			storyId: props.story.storyId,
			cardId: props.card._id,
			teamId: props.story.base.teamId || '',
			memberId: props.story.base.createdBy,
		};
	},
};

class Card extends React.Component<Props> {
	constructor(props: Props) {
		super(props);

		const cardId = this.props.card._id;
		const sHistory = new StoryHistory({ storyId: this.props.storyId });
		const isPlayedAlready = sHistory.isCardVisited({ cardId }); // for handle history back case

		log('didMount', cardId);

		this.setCardId();

		if (!this.props.isEditor && !isPlayedAlready) {
			sHistory.addCard({ cardId });

			if (!isPreview && this.props.story.settings.restrictions?.oncePerUser) {
				this.props.sendGameCheckpoint(this.props.storyId, cardId);
			}
		}

		defer(() => {
			document.documentElement.scrollTop = 0;
		});
	}

	componentDidMount() {
		this.sendEvents();
	}

	setCardId() {
		const card = new CardFacade(this.props.card);
		document.documentElement.dataset.cardId = card.cardId; /* used to handle global block show/hide rule per card */

		if (card.hasFeature.answer) {
			AnswerDelta.start(card.cardId);
		}

		if (card.hasFeature.submitButton) {
			SubmissionDelta.start(card.cardId);
		}
	}

	sendEvents = () => {
		if (events.isValidProps(this.props)) {
			ApiEvents.sendCardViewEvent({
				...events.getEventParams(this.props),
				gaUniversalId: this.props.story.settings.integrations?.ga?.gaUniversal,
			});
		}

		// Only Info card send story finish event automatically, other cards should send it manually on answer/submit
		const isLastCard = !this.props.nextDefaultCardId;
		const isCheckpointCard = this.props.user.checkpoint?.cardId === this.props.card._id;
		if (isLastCard && this.props.card.type === CARD_TYPE.INFO && !isCheckpointCard) {
			ApiEvents.sendFinishStoryEvent(events.getEventParams(this.props));
		}
	};

	render() {
		const { card, nextDefaultCardId = '', isEditor = false, treeOptions } = this.props;
		const Component = MAP[card.type];

		if (isEditor && !this.props.treeOptions) return null;

		return (
			<div id={CARD_ROOT_ID} className={css.card} onClick={this.props.onCardClick}>
				<CardHeightObserver />
				<Component
					data={card}
					symbols={this.props.story.symbols}
					nextDefaultCardId={nextDefaultCardId}
					isEditor={isEditor}
					treeOptions={treeOptions}
				/>
				{this.props.children}
			</div>
		);
	}
}

export default connector(Card);
